import { Component, ViewChild, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { VulnerabilityScanner } from '../../../../../../../core/models/entities';
import { environment } from '../../../../../../../../environments/environment';
import { VulnerabilityScannerForm, VulnerabilityScannerFormService } from './service/vulnerability-form.service';
import { VulnerabilityScannerRequest, VulnerabilityScannerService } from './service/vulnerability.service';
import DataTable from 'datatables.net-dt';

@Component({
  selector: 'app-vulnerability',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    InputsModule,
    LabelModule,
    ButtonsModule,
    DialogModule,
    ReactiveFormsModule,
    DatatableComponent,
  ],
  templateUrl: './vulnerability.component.html',
  styleUrl: './vulnerability.component.scss',
})
export class VulnerabilityComponent extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  public gridService = inject(VulnerabilityScannerService);
  public route = inject(ActivatedRoute);
  private formService = inject(VulnerabilityScannerFormService);
  private notificationService = inject(NotificationService);

  private baseUrl = `${environment.apiUrl}/v1/organization/administrative/vulnerabilityscanner`;
  public id!: number;

  public datatableUrl = signal<string>('');
  public formGroup = signal<FormGroup<VulnerabilityScannerForm> | undefined>(undefined);
  public isNew = signal<boolean>(false);
  public isSaving = signal<boolean>(false);
  private rows = new Map<string, VulnerabilityScanner>();

  public datatableColumns = [
    { data: 'ip_address', name: 'ip_address', title: 'IP Address', type: 'string', render: DataTable.render.text() },
    { data: 'notes', name: 'notes', title: 'Notes', type: 'string', render: DataTable.render.text() },
    {
      data: null,
      name: 'actions',
      title: 'Action',
      type: 'actions',
      orderable: false,
      render: (_data: unknown, _type: unknown, row: VulnerabilityScanner) => {
        this.rows.set(String(row.pk), row);
        return `
          <div class="d-flex gap-2">
            <button type="button" class="cyflare-btn-secondary cyflare-btn-sm action-edit" data-pk="${row.pk}">
              Edit
            </button>
            <button type="button" class="cyflare-btn-danger cyflare-btn-sm action-remove" data-pk="${row.pk}">
              Remove
            </button>
          </div>
        `;
      },
    },
  ];

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(
      () => {
        this.route.queryParams.pipe(filter((params) => params['organizationId'])).subscribe((data) => {
          this.id = parseInt(data['organizationId'], 10);
          this.gridService.url = `${this.baseUrl}(${this.id})/`;
          this.rows.clear();
          this.datatableUrl.set(this.gridService.url);
        });
      },
      { allowSignalWrites: true }
    );
  }

  public onTableClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement).closest('button');
    const pk = button?.getAttribute('data-pk');
    if (!button || !pk) {
      return;
    }

    if (button.classList.contains('action-edit')) {
      this.openEditor(this.rows.get(pk));
    } else if (button.classList.contains('action-remove')) {
      this.remove(pk);
    }
  }

  public openEditor(scanner?: VulnerabilityScanner): void {
    this.isNew.set(!scanner);
    this.formGroup.set(this.formService.createVulnerabilityScannerForm(scanner));
  }

  public closeEditor(): void {
    // The dialog close button and the backdrop both land here; ignore them
    // while a save is in flight so the user cannot walk away mid-request.
    if (this.isSaving()) {
      return;
    }
    this.formGroup.set(undefined);
  }

  public save(): void {
    const formGroup = this.formGroup();
    if (!formGroup || this.isSaving()) {
      return;
    }
    const formValue = formGroup.value;
    const payload: VulnerabilityScannerRequest = {
      organization_id: this.id,
      vulnerability_scanner: {
        pk: this.isNew() ? null : formValue?.pk!,
        ip_address: formValue?.ip_address!,
        notes: formValue?.notes!,
      },
    };

    const request = this.isNew()
      ? this.gridService.createVulnerabilityScanner(payload)
      : this.gridService.updateVulnerabilityScanner(payload.vulnerability_scanner);

    this.isSaving.set(true);
    request.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeEditor();
        this.table?.reload();
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error(error);
        this.showError('Failed to save the vulnerability scanner.');
      },
    });
  }

  private remove(pk: string): void {
    this.gridService.deleteVulnerabilityScanner(Number(pk)).subscribe({
      next: () => {
        this.table?.reload();
      },
      error: (error) => {
        console.error(error);
        this.showError('Failed to delete the vulnerability scanner.');
      },
    });
  }

  private showError(message: string): void {
    this.notificationService.show({
      content: message,
      cssClass: 'notification-error',
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'error', icon: true },
      closable: true,
      hideAfter: 5000,
    });
  }
}
